import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { Box, CircularProgress, Typography } from "@mui/material"
import { Accordion } from "minimals-template/components/accordion"
import { AmendsBoundaryEditor } from "authoring/routes/project/amends/components/amends-boundary-editor"
import { useAmendCollapse } from "authoring/routes/project/amends/components/use-amend-collapse"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { LEADING_SUMMARY_AMENDS_KEY } from "authoring/routes/project/amends/lib/constants/amends-keys"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorAmendsLoading } from "./hooks/use-schedule-editor-amends-loading"
import { AuthoringEditorRejectionBanner } from "./authoring-editor-rejection-banner"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import "lib/@hooks/useEvent"
import { hasAudienceAmendsContent } from "authoring/routes/project/amends/lib/audience-aware-amends.mjs"
import { CollectUnsavedMissingAmends } from "./schedule-editor-event-definitions.mjs"

export function LeadingSummaryAmendsEditor() {
    const { amendsContext } = useBoundContext()
    const amendsInitialLoading = useScheduleEditorAmendsLoading()
    const [openLeadingSummary, setOpenLeadingSummary] = useState(true)
    const { projectId, scheduleId } = useParams()
    const draftId = createDraftId(projectId, scheduleId)
    const { isCollapsed: leadingSummaryCollapsed, onToggleCollapsed: toggleLeadingSummary } = useAmendCollapse(draftId)
    const leadingSummaryState = amendsContext?.getAmendsState(LEADING_SUMMARY_AMENDS_KEY)
    const leadingSummaryOpen = leadingSummaryState?.isOpen ?? false
    const leadingSummaryEmpty =
        leadingSummaryOpen &&
        Boolean(leadingSummaryState?.isEditMode) &&
        !hasAudienceAmendsContent(leadingSummaryState?.noteData ?? {})
    const openingLeadingSummary = !amendsInitialLoading && !leadingSummaryOpen
    const showLoading = !leadingSummaryOpen && (amendsInitialLoading || openingLeadingSummary)

    useEffect(openLeadingSummaryEditor, [amendsInitialLoading, leadingSummaryOpen, amendsContext])
    CollectUnsavedMissingAmends(draftId).useEvent(reportEmptyLeadingSummary, [leadingSummaryEmpty])

    return (
        <Accordion
            title={<strong data-cy="leading-summary-amends-row">Leading Summary</strong>}
            open={openLeadingSummary}
            onChange={setOpenLeadingSummary}
            divider
        >
            <Box mt={2} mb={3}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Summarise the changes made to this schedule and what has been addressed.
                </Typography>

                <If truthy={showLoading}>
                    <Box
                        data-cy="leading-summary-amends-loading"
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 2,
                            mt: 1.5,
                            backgroundColor: "grey.200",
                            borderRadius: 1,
                        }}
                    >
                        <CircularProgress size={36} color="primary" />
                    </Box>
                    <Else>
                        <AmendsBoundaryEditor
                            amendsKey={LEADING_SUMMARY_AMENDS_KEY}
                            autoFocusOnOpen={false}
                            data-cy="leading-summary-amends"
                            collapsible
                            isCollapsed={leadingSummaryCollapsed}
                            onToggleCollapsed={toggleLeadingSummary}
                        />
                    </Else>
                </If>
                <AuthoringEditorRejectionBanner amendsKey={LEADING_SUMMARY_AMENDS_KEY} />
            </Box>
        </Accordion>
    )

    function openLeadingSummaryEditor() {
        if (amendsInitialLoading || leadingSummaryOpen || !amendsContext) return
        amendsContext.onOpenAmends(LEADING_SUMMARY_AMENDS_KEY)
    }

    function reportEmptyLeadingSummary({ amendsKeys }) {
        if (leadingSummaryEmpty) amendsKeys.push(LEADING_SUMMARY_AMENDS_KEY)
    }
}
